import { IconAdjust } from '@picsart/design-system/foundation/icons/IconAdjust';
import { IconAITools } from '@picsart/design-system/foundation/icons/IconAITools';
import { IconBackgroundRemove } from '@picsart/design-system/foundation/icons/IconBackgroundRemove';
import { IconCrop } from '@picsart/design-system/foundation/icons/IconCrop';
import { IconEffects } from '@picsart/design-system/foundation/icons/IconEffects';
import { IconElements } from '@picsart/design-system/foundation/icons/IconElements';
import { IconHomeOutline } from '@picsart/design-system/foundation/icons/IconHomeOutline';
import { IconObjectResize } from '@picsart/design-system/foundation/icons/IconObjectResize';
import { BackgroundColors } from '@picsart/design-system/foundation/colors';

import { IS_ONE_TOOL } from 'configs/constants';

import { CollapseStates } from '@picsart/design-system/components/EditorSlot';

import { isDesktopDevice, isMobileDevice } from '@picsart/design-system/utils/device';
import { createFloatingConfig } from 'pages/Batch/utils/bottomSheetConfig';
import type { IToasterInfo, UpscaleFactor } from 'types/index';

import colorHelpers from 'tinycolor2';
import type { BottomSheetConfig, IBatchTool } from 'types/store/index';

export const downloadFileName = 'Picsart Batch Editor' as const;

// NOTE: can't get package ID of current miniapp, have to hardcode it
export const BATCH_MINIAPP_ID = 'com.picsart.batch-editor';
export const BATCH_CLICK_ACTION = 'click';

export const UPSCALE_LIMIT_BIG_SIZE = 4096;
export const UPSCALE_LIMIT_SMALL_SIZE = 256;
export const ALLOWED_LIMIT_FILES = isDesktopDevice ? 50 : 25;
export const TOASTER_DURATION = 10000;
export const MOBILE_CANVAS_MAX_SIZE = 1400;
export const AI_MOBILE_SERVER_CONCURRENCY = 2;
export const API_WEB_CONCURRENCY = 7;
export const API_MOBILE_CONCURRENCY = 3;
export const UPLOAD_MOBILE_CONCURRENCY = 2;
export const UPLOAD_WEB_CONCURRENCY = 5;

export enum BATCH_TOOLS {
  background = 'background',
  aiBG = 'ai_bg',
  enhance = 'ai_enhance',
  watermark = 'watermark',
  upload = 'upload',
  resize = 'resize',
  crop = 'crop',
  removeBG_resize = 'removeBG_resize',
  wfx = 'wfx',
  adjust = 'adjust',
}

export enum ToolMonetization {
  Free = 'free',
  Credit = 'credit',
  Subscription = 'subscription',
}

// Every tool selection is an authorized action — only Upload/Home is free for logged-out users.
export const requiresAuthorization = (tool: BATCH_TOOLS | null): boolean => !!tool && tool !== BATCH_TOOLS.upload;

// Tools whose per-image effect is a live preview not yet baked into the result — driven by global state
// or a per-item preview canvas (resize target, background color/ratio, watermark/sticker). For these the
// preview and Apply follow the sticky working set, so deselecting an item keeps its changes instead of
// reverting them. Crop is intentionally excluded: its overlay is an interactive editing frame that must
// show only on the selected item (so it stays selection-based — deselecting hides the frame).
export const WORKING_SET_TOOLS: ReadonlySet<BATCH_TOOLS> = new Set([BATCH_TOOLS.resize, BATCH_TOOLS.background, BATCH_TOOLS.watermark]);

// Tools that auto-process images off the live selection (see useAutoApplyOnSelection): selecting an
// image kicks off an AI request for it. Deselection is blocked while any of these results is still
// generating, so a user can't drop an image mid-process.
export const AUTO_APPLY_TOOLS: ReadonlySet<BATCH_TOOLS> = new Set([BATCH_TOOLS.background, BATCH_TOOLS.enhance, BATCH_TOOLS.removeBG_resize]);

export const getToolMonetization = (tool: BATCH_TOOLS | null): ToolMonetization => {
  if (!tool) return ToolMonetization.Free;
  return batchToolsList.find(t => t.type === tool)?.monetization ?? ToolMonetization.Free;
};

export const isSubscriptionTool = (tool: BATCH_TOOLS | null): boolean => getToolMonetization(tool) === ToolMonetization.Subscription;

export enum BATCH_ACTIONS {
  removeBackground = 'remove_background',
  applyBackground = 'apply_background',
  generateAIBackground = 'generate_ai_background',
  applyEnhance = 'apply_enhance',
  aiEnhance = 'ai_enhance',
  applyCrop = 'apply_crop',
  watermark = 'watermark',
  resize = 'resize',
  wfx = 'wfx',
  applyWfx = 'apply_wfx',
  adjust = 'adjust',
  applyAdjust = 'apply_adjust',
  composedEnhance = 'composed_enhance',
}

export const ASYNC_TOOL_ACTION_MAP: Record<string, { tool: BATCH_TOOLS; action: BATCH_ACTIONS }> = {
  removeBackground: { tool: BATCH_TOOLS.background, action: BATCH_ACTIONS.removeBackground },
  aiEnhance: { tool: BATCH_TOOLS.enhance, action: BATCH_ACTIONS.aiEnhance },
};

export enum BATCH_ERROR {
  bigImage = 'bigImage',
  bigNumber = 'bigNumber',
  serverError = 'serverError',
  smallNumber = 'smallNumber',
  fileFormat = 'fileFormat',
  fileTooLarge = 'fileTooLarge',
  fileTooSmall = 'fileTooSmall',
  singleFileOnly = 'singleFileOnly',
  exceededFilesLimit = 'exceededFilesLimit',
  networkDisconnected = 'networkDisconnected',
  uploadFailed = 'uploadFailed',
  driveSave = 'driveSave',
}

export enum RESIZE_SIDE_ENUM {
  width = 'Width',
  height = 'Height',
  exactSize = 'Exact Size',
  socials = 'Socials',
}

export const resizeBySideParams = [RESIZE_SIDE_ENUM.width, RESIZE_SIDE_ENUM.height, RESIZE_SIDE_ENUM.exactSize, RESIZE_SIDE_ENUM.socials];

export const resizeBottomSheetSize: Record<RESIZE_SIDE_ENUM, BottomSheetConfig> = {
  [RESIZE_SIDE_ENUM.width]: createFloatingConfig(254),
  [RESIZE_SIDE_ENUM.height]: createFloatingConfig(254),
  [RESIZE_SIDE_ENUM.exactSize]: createFloatingConfig(360),
  [RESIZE_SIDE_ENUM.socials]: createFloatingConfig(400),
};

export enum UPSCALE_SIDE_ENUM {
  width = 'Width',
  height = 'Height',
}

export const upscaleBySideParams = [UPSCALE_SIDE_ENUM.width, UPSCALE_SIDE_ENUM.height];

export const batchToolsList: IBatchTool[] = [
  {
    id: 0,
    type: BATCH_TOOLS.upload,
    title: 'Home',
    dataTestId: 'upload-button',
    icon: IconHomeOutline,
    monetization: ToolMonetization.Free,
    isWithoutBackground: true,
    bottomSheetConfig: { behavior: 'static', collapseStates: { normal: { height: 0 } }, defaultSnapPoint: CollapseStates.Normal },
  },
  {
    id: 1,
    type: BATCH_TOOLS.crop,
    title: 'Crop',
    dataTestId: 'crop-button',
    icon: IconCrop,
    monetization: ToolMonetization.Subscription,
    isWithoutBackground: true,
    bottomSheetConfig: createFloatingConfig(240),
  },
  {
    id: 2,
    type: BATCH_TOOLS.wfx,
    title: 'Effects',
    icon: IconEffects,
    dataTestId: 'wfx-button',
    monetization: ToolMonetization.Subscription,
    bottomSheetConfig: createFloatingConfig(420),
    isWithoutBackground: true,
  },
  {
    id: 3,
    type: BATCH_TOOLS.background,
    title: 'Background',
    dataTestId: 'backgrounds-button',
    icon: IconBackgroundRemove,
    monetization: ToolMonetization.Credit,
    isWithoutBackground: true,
    bottomSheetConfig: createFloatingConfig(390),
  },
  {
    id: 4,
    type: BATCH_TOOLS.enhance,
    title: 'AI Enhance',
    dataTestId: 'ai-enhance-button',
    icon: IconAITools,
    monetization: ToolMonetization.Subscription,
    isWithoutBackground: true,
    bottomSheetConfig: createFloatingConfig(420),
  },
  {
    id: 5,
    type: BATCH_TOOLS.resize,
    title: isMobileDevice ? 'Fit' : 'Resize',
    dataTestId: 'resize-button',
    icon: IconObjectResize,
    monetization: ToolMonetization.Subscription,
    isWithoutBackground: true,
    bottomSheetConfig: createFloatingConfig(430),
  },
  {
    id: 6,
    type: BATCH_TOOLS.adjust,
    title: 'Adjust',
    icon: IconAdjust,
    dataTestId: 'adjust-button',
    bottomSheetConfig: createFloatingConfig(420),
    monetization: ToolMonetization.Subscription,
  },
  {
    id: 7,
    type: BATCH_TOOLS.watermark,
    title: 'Elements',
    icon: IconElements,
    dataTestId: 'watermark-button',
    monetization: ToolMonetization.Subscription,
    bottomSheetConfig: createFloatingConfig(352),
    isWithoutBackground: true,
  },
  ...(IS_ONE_TOOL
    ? [
        {
          id: 8,
          type: BATCH_TOOLS.removeBG_resize,
          title: 'Remove BG',
          icon: IconBackgroundRemove,
          dataTestId: 'remove-bg-resize-button',
          monetization: ToolMonetization.Credit,
          bottomSheetConfig: createFloatingConfig(230),
          isWithoutBackground: true,
        },
      ]
    : []),
];

export const upscaleMultiplyValues: { title: string; value: UpscaleFactor }[] = [
  {
    title: 'Two Times',
    value: 2,
  },
  {
    title: 'Four Times',
    value: 4,
  },
];

export const cropOrientationValues: string[] = ['Portrait', 'Landscape'];

export const cropAlignValues: {
  title: string;
  id: number;
  icon_name: string;
}[] = [
  {
    title: 'alignLeft',
    id: 0,
    icon_name: 'align-left-editor.svg',
  },
  {
    title: 'alignHorizontal',
    id: 1,
    icon_name: 'align-horizontal-centers-editor.svg',
  },
  {
    title: 'alignRight',
    id: 2,
    icon_name: 'align-right-editor.svg',
  },
  {
    title: 'alignTop',
    id: 3,
    icon_name: 'align-top-editor.svg',
  },
  {
    title: 'alignVertical',
    id: 4,
    icon_name: 'align-vertical-centers-editor.svg',
  },
  {
    title: 'alignBottom',
    id: 5,
    icon_name: 'align-bottom-editor.svg',
  },
];

export const toasterInfo: IToasterInfo = {
  error: {
    [BATCH_ERROR.bigImage]: {
      title: 'Image Too Large',
      text: 'Your image cannot exceed 4096 px after enhancing.',
    },
    [BATCH_ERROR.bigNumber]: {
      title: 'Target Height/Width Too Large',
      text: 'The target height/width number you entered is too large.',
    },
    [BATCH_ERROR.smallNumber]: {
      title: 'Target Height/Width Too Small',
      text: 'The target height/width number you entered is too small.',
    },
    [BATCH_ERROR.serverError]: {
      title: 'Unexpected Error Occurred',
      text: 'Some images were not processed correctly. Please try again.',
    },
    [BATCH_ERROR.fileFormat]: {
      title: 'Format not supported',
      text: 'Some images were not in the right format. Please upload only JPG, JPEG, PNG or WEBP.',
    },
    [BATCH_ERROR.fileTooLarge]: {
      title: 'File Too Large',
      text: 'One or more of your files exceed the maximum allowed size.',
    },
    [BATCH_ERROR.fileTooSmall]: {
      title: 'File Too Small',
      text: 'One or more of your files are below the minimum allowed size.',
    },
    [BATCH_ERROR.singleFileOnly]: {
      title: 'Only one file allowed',
      text: 'Please upload a single file.',
    },
    [BATCH_ERROR.exceededFilesLimit]: {
      title: 'Limit Exceeded',
      text: `There is limit of ${ALLOWED_LIMIT_FILES} images to upload and edit inside Batch Editor.`,
    },
    [BATCH_ERROR.networkDisconnected]: {
      title: 'No Internet Connection',
      text: 'Unable to continue batch editing. Check your internet connection and try again.',
    },
    [BATCH_ERROR.uploadFailed]: {
      title: 'Upload failed',
      text: 'We were unable to upload some of your images. Check your internet connection and try again.',
    },
    [BATCH_ERROR.driveSave]: {
      title: 'Error Saving Files',
      text: 'Something went wrong while saving your files to the Picsart Drive.',
    },
  },
};

export const resizeDefaultSizes = [800, 600];

export const inputParams = [
  {
    type: RESIZE_SIDE_ENUM.width,
    title: 'W',
  },
  {
    type: RESIZE_SIDE_ENUM.height,
    title: 'H',
  },
];

export const MAIN_GRID_MIN_SIZE = 260;

export const POSITIVE_NUMBER_REGEXP = /^(\d+)$|^$/;

export enum IMAGE_MIME_TYPES {
  PNG = 'image/png',
  JPEG = 'image/jpeg',
  WEBP = 'image/webp',
}

export enum EXPORT_DESTINATIONS {
  DEVICE = 'device',
  MY_FILES = 'my_files',
}

export const allowedUploadingTypes: string[] = [IMAGE_MIME_TYPES.JPEG, IMAGE_MIME_TYPES.PNG, IMAGE_MIME_TYPES.WEBP];

export const resizeMinSize = 32;
export const resizeMaxSize = 4096;

export const TRANSPARENT_OPTION = 'transparent';

export const TRANSPARENT_CHECKERBOARD_BACKGROUND = `conic-gradient(${BackgroundColors.tint1} 90deg, ${BackgroundColors.tint3} 0 180deg, ${BackgroundColors.tint1} 0 270deg, ${BackgroundColors.tint3} 0) 0 0 / 16px 16px`;

export const COLOR_PICKER_PIGMENT =
  'conic-gradient(from 90deg, rgb(207, 34, 238) -30.96deg, rgb(46, 133, 255) 3.16deg, rgb(41, 137, 254) 49.07deg, rgb(50, 255, 139) 98.61deg, rgb(242, 254, 51) 143.22deg, rgb(251, 102, 42) 203.27deg, rgb(235, 40, 245) 266.76deg, rgb(207, 34, 238) 329.04deg, rgb(46, 133, 255) 363.16deg)';

export const presetsColors = [
  null, // transparent BG
  {
    r: 255,
    g: 255,
    b: 255,
  },
  {
    r: 0,
    g: 0,
    b: 0,
  },
  {
    r: 158,
    g: 158,
    b: 158,
  },
  {
    r: 48,
    g: 79,
    b: 254,
  },
  {
    r: 64,
    g: 196,
    b: 255,
  },
  {
    r: 128,
    g: 216,
    b: 255,
  },
  {
    r: 100,
    g: 255,
    b: 218,
  },
  {
    r: 138,
    g: 206,
    b: 0,
  },
  {
    r: 238,
    g: 255,
    b: 65,
  },
  {
    r: 255,
    g: 215,
    b: 64,
  },
  {
    r: 255,
    g: 171,
    b: 0,
  },
  {
    r: 255,
    g: 158,
    b: 128,
  },
  {
    r: 255,
    g: 110,
    b: 64,
  },
  {
    r: 255,
    g: 61,
    b: 0,
  },
  {
    r: 255,
    g: 138,
    b: 128,
  },
  {
    r: 255,
    g: 23,
    b: 68,
  },
  {
    r: 234,
    g: 128,
    b: 252,
  },
  {
    r: 213,
    g: 0,
    b: 249,
  },
  {
    r: 170,
    g: 0,
    b: 255,
  },
];

export const presetColorsValues = presetsColors.map(x => (x ? colorHelpers(x).toRgbString() : TRANSPARENT_OPTION));

export const defaultWatermarkColor = {
  r: 255,
  g: 255,
  b: 255,
};

export const minTileInputSize = 1;
export const maxTileInputSize = 100;
export const minInputSpacing = 1;
export const maxInputSpacing = 100;
export const minLinePadding = 0;
export const maxLinePadding = 10;
export const minLineWidth = 0;
export const maxLineWidth = 10;
export const minOpacity = 0;
export const maxOpacity = 100;
export const minTileSize = 80;
export const maxTileSize = 300;
export const minSpaceSize = 60;
export const maxSpaceSize = 300;

export enum WATERMARK_SETTINGS {
  spacing = 'spacing',
  tileSize = 'tileSize',
  linePadding = 'linePadding',
  lineWidth = 'lineWidth',
  opacity = 'opacity',
}

export const watermarkSettingsData = [
  {
    type: WATERMARK_SETTINGS.spacing,
    value: 25,
    min: minInputSpacing,
    max: maxInputSpacing,
    label: 'Spacing',
  },
  {
    type: WATERMARK_SETTINGS.tileSize,
    value: 25,
    min: minTileInputSize,
    max: maxTileInputSize,
    label: 'Size',
  },
  {
    type: WATERMARK_SETTINGS.opacity,
    value: 25,
    min: minOpacity,
    max: maxOpacity,
    label: 'Opacity',
  },
  {
    type: WATERMARK_SETTINGS.linePadding,
    value: 5,
    min: minLinePadding,
    max: maxLinePadding,
    label: 'Line Length',
  },
  {
    type: WATERMARK_SETTINGS.lineWidth,
    value: 5,
    min: minLineWidth,
    max: maxLineWidth,
    label: 'Line Width',
  },
];

export interface IWatermarkSetting {
  type: WATERMARK_SETTINGS;
  value: number;
  min: number;
  max: number;
  label: string;
}

export const BATCH_TOOLTIP = {
  upscale:
    'Use this tool to enlarge low resolution images without any quality loss. You can upscale your images by up to 4 times their original size.' +
    ' Which is great for things like printing low resolution images onto bigger mediums like posters and billboards.',
  watermark: 'Use this tool to apply your own watermark to multiple images simultaneously. Protect and claim full ownership of your content by watermarking your images.',
};

export { RATIO_CARD } from './ratioCards';
